import type { ErrorEvent } from '@sentry/vue'
import { getAxiosStatus } from './errorHandling'

/**
 * The parts of a rejected axios request this module reads. Matched
 * structurally rather than via axios' own `isAxiosError` marker, consistently
 * with `errorHandling.ts`: the marker is stamped by the axios instance that
 * created the error, so it is absent on rejections that cross a bundle
 * boundary or come from a test double, while the config shape is what actually
 * describes the failed request.
 */
interface AxiosRequestShape {
  config?: { url?: unknown; method?: unknown }
  code?: unknown
}

/** Placeholder standing in for a segment that varies per request. */
const VARIABLE_SEGMENT = '{id}'

/** `550e8400-e29b-41d4-a716-446655440000` — the overwhelmingly common id form. */
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** A database id or a year/week number. */
const NUMERIC_SEGMENT = /^\d+$/

/** `2026-07-27` — agenda and availability endpoints put a date in the path. */
const DATE_SEGMENT = /^\d{4}-\d{2}-\d{2}$/

/**
 * A hex token: invitation and magic-link tokens that are not UUIDs. The 16-char
 * floor keeps it clear of real path segments — no endpoint of ours is a run of
 * 16+ `a`–`f` characters.
 */
const HEX_TOKEN_SEGMENT = /^[0-9a-f]{16,}$/i

/**
 * A base64url-ish random token. Requires all three character classes so that
 * long kebab-case segments are not mistaken for tokens: `form-submissions` has
 * no digit, `dsm5-diagnoses` has no uppercase, and a random 16+ character token
 * realistically has both.
 */
const OPAQUE_TOKEN_SEGMENT = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z0-9_-]{16,}$/

function isVariableSegment(segment: string): boolean {
  return (
    UUID_SEGMENT.test(segment) ||
    NUMERIC_SEGMENT.test(segment) ||
    DATE_SEGMENT.test(segment) ||
    HEX_TOKEN_SEGMENT.test(segment) ||
    OPAQUE_TOKEN_SEGMENT.test(segment)
  )
}

/**
 * Rewrites a request URL into the route it belongs to:
 * `https://api.scrivio.nl/api/v1/forms/2f1c…/submissions?page=0` becomes
 * `/api/v1/forms/{id}/submissions`.
 *
 * Grouping on the raw URL would invert the problem this module exists to solve
 * — instead of one issue for every axios failure, one issue per UUID. Segments
 * are classified by shape because the failed request carries no route template:
 * axios only knows the URL that was actually sent.
 */
export function templateRequestPath(url: string): string {
  const [beforeQuery = ''] = url.split(/[?#]/)
  // Drop `scheme://host:port`, leaving an absolute URL and a relative one
  // (built against axios' `baseURL`) with the same shape.
  return beforeQuery
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '')
    .split('/')
    .map((segment) => (isVariableSegment(segment) ? VARIABLE_SEGMENT : segment))
    .join('/')
}

/**
 * Returns the Sentry fingerprint identifying an axios failure, or `undefined`
 * when the error did not come from an axios request.
 *
 * Every axios rejection captured by `captureConsoleIntegration` has a stack
 * consisting only of axios internals (`xhr.js` `onloadend`, `settle.js`
 * `settle`), so Sentry's default stack-based grouping collapsed all of them —
 * a benign 410, a handled 409 and a genuine 500 — into a single issue. Resolving
 * that issue then hid every future member of it from `is:unresolved`, the query
 * both triage automation (ADR-017) and humans use, so a production 500 went
 * unnoticed for months (SCRIVIO-FE-2A).
 *
 * The failure is identified by what actually distinguishes one from another:
 * status (falling back to the axios error code when the request got no
 * response), method and templated path.
 */
export function getAxiosEventFingerprint(error: unknown): string[] | undefined {
  if (error === null || typeof error !== 'object') return undefined
  const { config, code } = error as AxiosRequestShape
  if (config === null || typeof config !== 'object' || typeof config.url !== 'string') {
    return undefined
  }

  const status = getAxiosStatus(error)
  const failure = status !== undefined ? String(status) : typeof code === 'string' ? code : 'unknown'
  const method = typeof config.method === 'string' ? config.method.toUpperCase() : 'UNKNOWN'
  return ['axios', failure, method, templateRequestPath(config.url)]
}

/**
 * Gives an axios event its own Sentry group, and a title naming the request
 * that failed instead of the interchangeable "Request failed with status code
 * 500". Any other event is returned untouched.
 */
export function applyAxiosEventGrouping(event: ErrorEvent, error: unknown): ErrorEvent {
  // A call site that stated its own group knows something this cannot: which of
  // an endpoint's callers failed. Grouping by status/method/path is the answer
  // for a stack-less axios rejection nobody claimed, and it is the wrong one for
  // an event a caller captured deliberately — it would fold the agenda's
  // appointments load back in with the dashboard's, which is the state #4172
  // found the frontend project in.
  if (event.fingerprint?.length) return event

  const fingerprint = getAxiosEventFingerprint(error)
  if (!fingerprint) return event

  const [, failure, method, path] = fingerprint
  event.fingerprint = fingerprint

  // The issue list shows the exception value, so this is what makes a group
  // triageable without opening it. `values` is ordered oldest to newest, so the
  // last entry is the one Sentry titles the issue with.
  const values = event.exception?.values
  const titled = values?.[values.length - 1]
  if (titled) titled.value = `${failure} ${method} ${path}`

  return event
}
